import { useMemo, useState, type ReactNode } from 'react'
import { CaretDown, Check, MagnifyingGlass } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { Sheet } from './Sheet'
import { Input } from './Input'
import { controlBorder } from './controlStyles'
import { useFieldControl } from './fieldContext'

export interface PickerOption<V extends string = string> {
  value: V
  label: ReactNode
  /** Plain text used for search; defaults to label when it is a string. */
  searchText?: string
  description?: ReactNode
  /** Right-aligned extra (a badge, a stamp date). */
  meta?: ReactNode
  disabled?: boolean
}

export interface PickerSheetProps<V extends string = string> {
  title: ReactNode
  options: ReadonlyArray<PickerOption<V>>
  value: V | null
  onChange: (value: V) => void
  placeholder?: ReactNode
  /** Show a search box (default: when more than 8 options). */
  searchable?: boolean
  disabled?: boolean
}

/**
 * Phone picker: a large trigger that opens a bottom sheet of big rows. Use for scales, sack
 * types, crops — anything picked with a thumb.
 */
export function PickerSheet<V extends string = string>({
  title,
  options,
  value,
  onChange,
  placeholder,
  searchable,
  disabled,
}: PickerSheetProps<V>) {
  const { t } = useI18n()
  const field = useFieldControl()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const selected = options.find((o) => o.value === value)
  const showSearch = searchable ?? options.length > 8

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return options
    return options.filter((o) =>
      (o.searchText ?? (typeof o.label === 'string' ? o.label : '')).toLowerCase().includes(needle),
    )
  }, [options, q])

  return (
    <>
      <button
        type="button"
        id={field?.id}
        aria-haspopup="dialog"
        aria-describedby={field?.describedBy}
        aria-invalid={field?.invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(
          'flex min-h-touch w-full items-center justify-between gap-3 rounded-chip border bg-surface px-3.5 text-left text-[17px] transition-colors disabled:cursor-not-allowed disabled:bg-paper-sunk',
          controlBorder(field?.invalid ?? false),
        )}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-muted')}>
          {selected ? selected.label : (placeholder ?? t('field.select'))}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {selected?.meta}
          <CaretDown aria-hidden="true" className="text-muted" />
        </span>
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={title}>
        {showSearch && (
          <div className="mb-3">
            <Input
              size="touch"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('common.search')}
              aria-label={t('common.search')}
              leading={<MagnifyingGlass />}
              data-autofocus
            />
          </div>
        )}
        <ul
          role="listbox"
          aria-label={typeof title === 'string' ? title : undefined}
          className="-mx-1 flex flex-col"
        >
          {filtered.map((o) => {
            const isSel = o.value === value
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isSel}
                aria-disabled={o.disabled || undefined}
              >
                <button
                  type="button"
                  disabled={o.disabled}
                  onClick={() => {
                    onChange(o.value)
                    setOpen(false)
                    setQ('')
                  }}
                  className={cn(
                    'flex min-h-touch-lg w-full items-center gap-3 rounded-chip px-3 py-2 text-left transition-colors hover:bg-ink/5 disabled:opacity-40',
                    isSel && 'bg-amber-wash',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[16px] text-ink">{o.label}</span>
                    {o.description && (
                      <span className="block text-[13px] text-muted">{o.description}</span>
                    )}
                  </span>
                  {o.meta}
                  {isSel && (
                    <Check weight="bold" className="shrink-0 text-amber-dark" aria-hidden="true" />
                  )}
                </button>
              </li>
            )
          })}
          {filtered.length === 0 && (
            <li className="px-3 py-6 text-center text-[14px] text-muted">
              {t('common.noResults')}
            </li>
          )}
        </ul>
      </Sheet>
    </>
  )
}
